import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api/client';
import { reportAnalyticsError } from '@/lib/api/analytics-error-handler';
import { detectBrowserName, detectDeviceType, truncate } from '@/lib/utils/analytics';
import logger from '@/lib/utils/logger';
import { getOrCreateAnonymousId } from '@/lib/utils/analytics-session';

interface AnonymousSessionResponse {
  success: boolean;
  session_id: number;
  anonymous_id: string;
}

interface AnonymousEventResponse {
  success: boolean;
  event_id: number;
}

const STABLE_ROUTE_DELAY_MS = 150;

function getUtmParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const source = params.get('utm_source');
  const medium = params.get('utm_medium');
  const campaign = params.get('utm_campaign');

  return {
    ...(source === null ? {} : { utm_source: source }),
    ...(medium === null ? {} : { utm_medium: medium }),
    ...(campaign === null ? {} : { utm_campaign: campaign }),
  };
}

function reportTrackingFailure(errorType: string, error: unknown): void {
  const normalizedError = error instanceof Error ? error : new Error(String(error));
  logger.error(`Anonymous analytics failed: ${errorType}`, normalizedError);
  void reportAnalyticsError(errorType, normalizedError);
}

/** Owns the single pre-auth analytics session and its route events. */
export function AnonymousTrackingProvider(): null {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const [anonymousId, setAnonymousId] = useState<string | null>(null);
  const lastTrackedPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading) return;

    if (isAuthenticated) {
      setAnonymousId(null);
      lastTrackedPathRef.current = null;
      return;
    }

    let cancelled = false;

    async function createSession(): Promise<void> {
      const id = getOrCreateAnonymousId();
      const response = await apiClient.post<AnonymousSessionResponse>('/analytics/anonymous/sessions', {
        anonymous_id: id,
        device_type: detectDeviceType(navigator.userAgent),
        browser: detectBrowserName(navigator.userAgent),
        ...(document.referrer === '' ? {} : { referrer: truncate(document.referrer, 500) }),
        landing_page: truncate(`${window.location.pathname}${window.location.search}`, 500),
        ...getUtmParams(),
      });

      if (!response.data.success || response.data.anonymous_id !== id) {
        throw new Error('Anonymous analytics session returned an invalid response');
      }

      if (!cancelled) setAnonymousId(id);
    }

    void createSession().catch((error: unknown) => {
      reportTrackingFailure('anonymous_session_create_failed', error);
    });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    if (isLoading || isAuthenticated || anonymousId === null) return;

    const pageUrl = truncate(`${location.pathname}${location.search}`, 500);
    if (pageUrl === undefined || lastTrackedPathRef.current === pageUrl) return;
    const stablePageUrl: string = pageUrl;

    async function trackPageView(): Promise<void> {
      const eventType = lastTrackedPathRef.current === null ? 'landing_page_view' : 'page_view';
      lastTrackedPathRef.current = stablePageUrl;

      const response = await apiClient.post<AnonymousEventResponse>('/analytics/anonymous/events', {
        anonymous_id: anonymousId,
        event_type: eventType,
        event_category: 'navigation',
        page_url: stablePageUrl,
        ...(document.referrer === '' ? {} : { referrer: truncate(document.referrer, 500) }),
      });

      if (!response.data.success) {
        throw new Error('Anonymous analytics event returned an invalid response');
      }
    }

    const timer = window.setTimeout(() => {
      void trackPageView().catch((error: unknown) => {
        reportTrackingFailure('anonymous_page_view_failed', error);
      });
    }, STABLE_ROUTE_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [anonymousId, isAuthenticated, isLoading, location.pathname, location.search]);

  return null;
}

export default AnonymousTrackingProvider;
